// Groups a saved dashboard's widgets into three sections — KPIs, Charts,
// Tables — instead of one flat grid.
//
// Fixes two real problems found comparing a real dashboard's screenshot
// vs its PDF export (5 Aug 2026, see .kiro/steering/ai-chat-dashboard-
// tracker.md): (1) the old single `auto-fit` grid's column count depends
// on available width, which differs between a screen viewport and a
// fixed print page — same CSS, different layout; (2) CSS Grid stretches
// every cell in a row to match the tallest sibling, so a short KPI card
// sitting in the same row as a tall chart/table rendered as an
// almost-empty box. Grouping by viz_type means a card only ever
// stretches against same-type siblings.
//
// KPI row-chunking (6 Aug 2026, replaces an earlier CSS-only attempt —
// see ai-chat-dashboard-tracker.md): a SINGLE CSS Grid/Flex container for
// every KPI card can't make an uneven last row fill the width, because
// grid tracks (and a single flex-wrap line's basis) are shared across
// every row — a wrapped remainder row inherits the SAME per-card width
// computed for the full rows above it, leaving dead space next to the
// last card(s) instead of them growing to fill their own row. Verified
// with a real screenshot on 7 KPIs before this fix.
// The actual fix: chunk the KPI list into groups of at most
// MAX_KPI_COLS_PER_ROW, and render EACH chunk as its OWN independent
// flex row (own <div className="ai-chat-kpi-row">). Every row's cards
// use `flex: 1` (ai-chat.css) so they divide THAT row's width evenly —
// a lone remainder card gets `flex: 1` with no siblings, so it fills
// 100% of the row width, not some leftover fraction of a shared grid
// track. This is what makes 1 KPI a full-width card, 5 KPIs a 4-card row
// + a full-width 1-card row, 7 KPIs a 4-card row + an evenly-thirded
// 3-card row, etc. — generic for any count, no per-count special-casing.
const MAX_KPI_COLS_PER_ROW = 4;

function chunk(items, size) {
  const rows = [];
  for (let i = 0; i < items.length; i += size) {
    rows.push(items.slice(i, i + size));
  }
  return rows;
}

// Section order (KPIs -> Charts -> Tables) matches the natural
// "headline numbers, then trends/comparisons, then detail" reading order
// used in the reference QuickSight-style dashboard and the sample HTML
// the redesign borrowed section styling from (eyebrow-labelled numbered
// sections). Only the LAYOUT ideas were borrowed; nothing about how
// widgets are rendered changed — still real Cloudscape components via
// WidgetRenderer, per the locked "never raw HTML" decision.

import React from 'react';
import WidgetRenderer from './WidgetRenderer.jsx';

export default function DashboardSections({ widgets }) {
  const kpis = widgets.filter((w) => w.viz_type === 'single_stat');
  const charts = widgets.filter((w) => w.viz_type === 'bar' || w.viz_type === 'line');
  const tables = widgets.filter((w) => w.viz_type === 'table');
  const kpiRows = chunk(kpis, MAX_KPI_COLS_PER_ROW);

  return (
    <>
      {kpis.length > 0 && (
        <div className="ai-chat-dashboard-section">
          <div className="ai-chat-dashboard-section-header">
            <span className="ai-chat-dashboard-section-eyebrow">01 — Overview</span>
            <span className="ai-chat-dashboard-section-title">Headline metrics</span>
          </div>
          <div className="ai-chat-kpi-grid">
            {kpiRows.map((row, rowIndex) => (
              <div className="ai-chat-kpi-row" key={rowIndex}>
                {row.map((w) => (
                  <WidgetRenderer key={w.widget_id} widget={w} />
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {charts.length > 0 && (
        <div className="ai-chat-dashboard-section">
          <div className="ai-chat-dashboard-section-header">
            <span className="ai-chat-dashboard-section-eyebrow">02 — Trends &amp; comparisons</span>
            <span className="ai-chat-dashboard-section-title">Charts</span>
          </div>
          <div className="ai-chat-chart-grid">
            {charts.filter((w) => !w.viz_config?.full_width).map((w) => <WidgetRenderer key={w.widget_id} widget={w} />)}
          </div>
          {/* Full-width charts rendered outside the grid, one per row */}
          {charts.filter((w) => w.viz_config?.full_width).map((w) => (
            <div key={w.widget_id} className="ai-chat-widget-full-width">
              <WidgetRenderer widget={w} />
            </div>
          ))}
        </div>
      )}

      {tables.length > 0 && (
        <div className="ai-chat-dashboard-section">
          <div className="ai-chat-dashboard-section-header">
            <span className="ai-chat-dashboard-section-eyebrow">03 — Detail</span>
            <span className="ai-chat-dashboard-section-title">Tables</span>
          </div>
          <div className="ai-chat-table-grid">
            {tables.filter((w) => !w.viz_config?.full_width).map((w) => <WidgetRenderer key={w.widget_id} widget={w} />)}
          </div>
          {/* Full-width tables rendered outside the grid, one per row */}
          {tables.filter((w) => w.viz_config?.full_width).map((w) => (
            <div key={w.widget_id} className="ai-chat-widget-full-width">
              <WidgetRenderer widget={w} />
            </div>
          ))}
        </div>
      )}
    </>
  );
}
